import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Pencil, Plus, Trash2, X } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { fmtGNF } from "@/lib/format";
import { ABtn, AInput, ALabel, Panel } from "./AdminLayout";

export function AdminCategories() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState(null);

  const load = () => api.get("/categories", { params: { all: true } }).then((r) => setItems(r.data));

  useEffect(() => {
    load();
  }, []);

  const save = async () => {
    try {
      const payload = { name: form.name, icon: form.icon || "Package", is_active: form.is_active };
      if (form.id) await api.put(`/admin/categories/${form.id}`, payload);
      else await api.post("/admin/categories", payload);
      toast.success(form.id ? "Catégorie modifiée" : "Catégorie créée");
      setForm(null);
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const remove = async (c) => {
    if (!window.confirm(`Supprimer la catégorie « ${c.name} » ?`)) return;
    try {
      await api.delete(`/admin/categories/${c.id}`);
      toast.success("Catégorie supprimée");
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  return (
    <Panel
      title="Catégories"
      subtitle={`${items.length} catégorie(s)`}
      actions={
        <ABtn onClick={() => setForm({ name: "", icon: "Package", is_active: true })} data-testid="admin-new-category-button">
          <Plus className="h-4 w-4" /> Nouvelle catégorie
        </ABtn>
      }
      testid="admin-categories-page"
    >
      <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
        {items.map((c) => (
          <div key={c.id} className="flex items-center justify-between rounded-xl border border-slate-800 p-3" data-testid={`admin-category-row-${c.slug}`}>
            <div>
              <p className="text-sm font-semibold text-white">{c.name}</p>
              <p className="font-num text-[11px] text-slate-400">
                {c.product_count} produit(s) {!c.is_active && "· inactive"}
              </p>
            </div>
            <div className="flex gap-1">
              <ABtn variant="ghost" onClick={() => setForm(c)} data-testid={`admin-edit-category-${c.slug}`}>
                <Pencil className="h-3.5 w-3.5" />
              </ABtn>
              <ABtn variant="danger" onClick={() => remove(c)} data-testid={`admin-delete-category-${c.slug}`}>
                <Trash2 className="h-3.5 w-3.5" />
              </ABtn>
            </div>
          </div>
        ))}
      </div>

      {form && (
        <div className="fixed inset-0 z-50 grid place-items-center bg-black/60 p-4" data-testid="admin-category-modal">
          <div className="w-full max-w-md rounded-2xl border border-slate-800 bg-slate-900 p-5">
            <div className="flex items-center justify-between">
              <h3 className="font-display text-lg font-bold text-white">{form.id ? "Modifier" : "Nouvelle catégorie"}</h3>
              <ABtn variant="ghost" onClick={() => setForm(null)}><X className="h-4 w-4" /></ABtn>
            </div>
            <div className="mt-4 space-y-3">
              <div>
                <ALabel>Nom *</ALabel>
                <AInput value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="category-form-name" />
              </div>
              <label className="flex items-center gap-2 text-sm text-slate-300">
                <input type="checkbox" checked={form.is_active} onChange={(e) => setForm({ ...form, is_active: e.target.checked })} data-testid="category-form-active" />
                Catégorie active
              </label>
            </div>
            <div className="mt-5 flex justify-end gap-2">
              <ABtn variant="ghost" onClick={() => setForm(null)}>Annuler</ABtn>
              <ABtn onClick={save} data-testid="category-form-save">Enregistrer</ABtn>
            </div>
          </div>
        </div>
      )}
    </Panel>
  );
}

export function AdminZones() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState(null);

  const load = () => api.get("/delivery-zones", { params: { all: true } }).then((r) => setItems(r.data));

  useEffect(() => {
    load();
  }, []);

  const save = async () => {
    try {
      const payload = { name: form.name, fee: Number(form.fee) || 0, eta: form.eta || "", is_active: form.is_active, free_above: Number(form.free_above) || 0 };
      if (form.id) await api.put(`/admin/delivery-zones/${form.id}`, payload);
      else await api.post("/admin/delivery-zones", payload);
      toast.success("Zone enregistrée");
      setForm(null);
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const remove = async (z) => {
    if (!window.confirm(`Supprimer la zone « ${z.name} » ?`)) return;
    try {
      await api.delete(`/admin/delivery-zones/${z.id}`);
      toast.success("Zone supprimée");
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  return (
    <Panel
      title="Zones de livraison"
      subtitle="Tarifs et délais par zone"
      actions={
        <ABtn onClick={() => setForm({ name: "", fee: 0, eta: "", is_active: true, free_above: 0 })} data-testid="admin-new-zone-button">
          <Plus className="h-4 w-4" /> Nouvelle zone
        </ABtn>
      }
      testid="admin-zones-page"
    >
      <div className="overflow-x-auto">
        <table className="w-full min-w-[600px] text-left text-sm">
          <thead className="text-[11px] uppercase tracking-wider text-slate-400">
            <tr className="border-b border-slate-800">
              <th className="py-2">Zone</th>
              <th className="py-2 text-right">Tarif</th>
              <th className="py-2">Délai estimé</th>
              <th className="py-2">Statut</th>
              <th className="py-2" />
            </tr>
          </thead>
          <tbody>
            {items.map((z) => (
              <tr key={z.id} className="border-b border-slate-800/60" data-testid={`admin-zone-row-${z.id}`}>
                <td className="py-2 font-medium text-white">{z.name}</td>
                <td className="font-num py-2 text-right text-emerald-400">
                  {z.fee > 0 ? fmtGNF(z.fee) : "Gratuit"}
                  {z.free_above > 0 && <span className="block text-[10px] text-slate-400">offerte dès {fmtGNF(z.free_above)}</span>}
                </td>
                <td className="py-2 text-slate-300">{z.eta || "—"}</td>
                <td className="py-2">
                  <span className={`ds-chip ${z.is_active ? "border-emerald-700 bg-emerald-950 text-emerald-300" : "border-slate-700 bg-slate-800 text-slate-400"}`}>
                    {z.is_active ? "Active" : "Désactivée"}
                  </span>
                </td>
                <td className="py-2">
                  <div className="flex justify-end gap-1">
                    <ABtn variant="ghost" onClick={() => setForm(z)} data-testid={`admin-edit-zone-${z.id}`}>
                      <Pencil className="h-3.5 w-3.5" />
                    </ABtn>
                    <ABtn variant="danger" onClick={() => remove(z)} data-testid={`admin-delete-zone-${z.id}`}>
                      <Trash2 className="h-3.5 w-3.5" />
                    </ABtn>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {form && (
        <div className="fixed inset-0 z-50 grid place-items-center bg-black/60 p-4" data-testid="admin-zone-modal">
          <div className="w-full max-w-md rounded-2xl border border-slate-800 bg-slate-900 p-5">
            <div className="flex items-center justify-between">
              <h3 className="font-display text-lg font-bold text-white">{form.id ? "Modifier la zone" : "Nouvelle zone"}</h3>
              <ABtn variant="ghost" onClick={() => setForm(null)}><X className="h-4 w-4" /></ABtn>
            </div>
            <div className="mt-4 space-y-3">
              <div>
                <ALabel>Nom de la zone *</ALabel>
                <AInput value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="zone-form-name" />
              </div>
              <div>
                <ALabel>Frais de livraison (GNF)</ALabel>
                <AInput type="number" value={form.fee} onChange={(e) => setForm({ ...form, fee: e.target.value })} data-testid="zone-form-fee" />
              </div>
              <div>
                <ALabel>Livraison offerte à partir de (GNF, 0 = désactivé)</ALabel>
                <AInput type="number" value={form.free_above ?? 0} onChange={(e) => setForm({ ...form, free_above: e.target.value })} data-testid="zone-form-free-above" />
              </div>
              <div>
                <ALabel>Délai estimé</ALabel>
                <AInput value={form.eta || ""} onChange={(e) => setForm({ ...form, eta: e.target.value })} placeholder="24 à 48 heures" data-testid="zone-form-eta" />
              </div>
              <label className="flex items-center gap-2 text-sm text-slate-300">
                <input type="checkbox" checked={form.is_active} onChange={(e) => setForm({ ...form, is_active: e.target.checked })} data-testid="zone-form-active" />
                Zone active
              </label>
            </div>
            <div className="mt-5 flex justify-end gap-2">
              <ABtn variant="ghost" onClick={() => setForm(null)}>Annuler</ABtn>
              <ABtn onClick={save} data-testid="zone-form-save">Enregistrer</ABtn>
            </div>
          </div>
        </div>
      )}
    </Panel>
  );
}
